import React, { useState } from 'react';
import {
  Box, Typography, Paper, Switch, FormControlLabel, TextField, Grid, Divider, Tabs, Tab,
  InputAdornment, Checkbox, FormGroup, FormLabel
} from '@mui/material';
import { numField, vehicleTypesFrom, PLAN_FAMILIES, PLAN_KEYS, round2, isBlank } from './shared';

const CLASS_GROUPS = [
  { key: 'light', label: 'Light (cars, SUVs, pickups, vans, motorcycles)' },
  { key: 'medium', label: 'Medium (towables)' },
  { key: 'heavy', label: 'Heavy (motorhomes, fifth wheels)' }
];

/** Every plan key present, in family order, so the cards never go missing on a partial config. */
const normalizePlans = (plans) => PLAN_KEYS.map((key) => {
  const family = PLAN_FAMILIES.find((f) => f.planKeys.includes(key)).key;
  const existing = (plans || []).find((p) => p.key === key);
  return {
    key,
    family,
    name: '',
    tagline: '',
    description: '',
    enabled: true,
    coveredVehicleTypes: [],
    ...(existing || {}),
    prices: { monthly: '', annual: '', ...(existing?.prices || {}) },
    benefits: {
      fuelGallons: 5, mobileMechanic: false, tripInterruptionCap: 0, tireWheelCap: 0, tireWheelPerYear: 0,
      adFree: true, household: true, ...(existing?.benefits || {})
    },
    stripe: existing?.stripe || {}
  };
});

const introPrice = (price, intro) => {
  if (!intro?.enabled || isBlank(price) || isBlank(intro.percentOff)) return null;
  const n = Number(price);
  if (Number.isNaN(n)) return null;
  return round2(n * (1 - Number(intro.percentOff) / 100));
};

function PlanCard({ plan, intro, vehicleTypes, onChange }) {
  const set = (field, value) => onChange({ ...plan, [field]: value });
  const setPrice = (field, value) => onChange({ ...plan, prices: { ...plan.prices, [field]: value } });
  const setBenefit = (field, value) => onChange({ ...plan, benefits: { ...plan.benefits, [field]: value } });
  const toggleType = (key, checked) => {
    const current = plan.coveredVehicleTypes || [];
    set('coveredVehicleTypes', checked ? [...new Set([...current, key])] : current.filter((k) => k !== key));
  };

  const b = plan.benefits || {};
  const stripe = plan.stripe || {};
  const introMonthly = introPrice(plan.prices?.monthly, intro);
  const introAnnual = introPrice(plan.prices?.annual, intro);
  const unlimited = isBlank(plan.includedCallsPerYear);

  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box>
          <Typography variant="subtitle1">{plan.name || plan.key}</Typography>
          <Typography variant="caption" color="text.secondary">{plan.key}</Typography>
        </Box>
        <FormControlLabel
          control={<Switch size="small" checked={plan.enabled !== false} onChange={(e) => set('enabled', e.target.checked)} />}
          label="Enabled"
        />
      </Box>

      <TextField
        fullWidth size="small" label="Name" value={plan.name || ''} sx={{ mb: 1.5 }}
        onChange={(e) => set('name', e.target.value)}
      />
      <TextField
        fullWidth size="small" label="Tagline" value={plan.tagline || ''} sx={{ mb: 1.5 }}
        onChange={(e) => set('tagline', e.target.value)}
      />
      <TextField
        fullWidth size="small" label="Description" value={plan.description || ''} sx={{ mb: 1.5 }}
        multiline minRows={3}
        onChange={(e) => set('description', e.target.value)}
      />

      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        <Grid item xs={introMonthly != null ? 7 : 12}>
          <TextField
            {...numField({ label: 'Monthly price' })} value={plan.prices?.monthly ?? ''}
            onChange={(e) => setPrice('monthly', e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            inputProps={{ min: 0, step: 0.01 }}
          />
        </Grid>
        {introMonthly != null && (
          <Grid item xs={5}>
            <TextField
              size="small" fullWidth label="Intro / mo" value={`$${introMonthly.toFixed(2)}`}
              InputProps={{ readOnly: true }} helperText="First 12 payments"
            />
          </Grid>
        )}
        <Grid item xs={introAnnual != null ? 7 : 12}>
          <TextField
            {...numField({ label: 'Annual price' })} value={plan.prices?.annual ?? ''}
            onChange={(e) => setPrice('annual', e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            inputProps={{ min: 0, step: 0.01 }}
          />
        </Grid>
        {introAnnual != null && (
          <Grid item xs={5}>
            <TextField
              size="small" fullWidth label="Intro / yr" value={`$${introAnnual.toFixed(2)}`}
              InputProps={{ readOnly: true }} helperText="First year"
            />
          </Grid>
        )}
      </Grid>

      <TextField
        {...numField({ label: 'Included calls / year' })} value={plan.includedCallsPerYear ?? ''} sx={{ mb: 1.5 }}
        onChange={(e) => set('includedCallsPerYear', e.target.value)}
        inputProps={{ min: 0, max: 1000 }}
        helperText={unlimited ? 'Blank = Unlimited' : 'Leave blank for unlimited'}
      />
      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        <Grid item xs={6}>
          <TextField
            {...numField({ label: 'Extra-call discount' })} value={plan.extraCallDiscountPercent ?? ''}
            onChange={(e) => set('extraCallDiscountPercent', e.target.value)}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            inputProps={{ min: 0, max: 100 }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            {...numField({ label: 'Tow miles / call' })} value={plan.towMilesPerCall ?? ''}
            onChange={(e) => set('towMilesPerCall', e.target.value)}
            inputProps={{ min: 0, max: 1000 }}
            helperText="0 = nearest shop"
          />
        </Grid>
      </Grid>

      <FormLabel component="legend" sx={{ fontSize: 13, mb: 0.5 }}>Covered vehicle types</FormLabel>
      {CLASS_GROUPS.map((group) => {
        const types = vehicleTypes.filter((t) => t.class === group.key);
        if (types.length === 0) return null;
        return (
          <Box key={group.key} sx={{ mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">{group.label}</Typography>
            <FormGroup row>
              {types.map((t) => (
                <FormControlLabel
                  key={t.key}
                  sx={{ mr: 1.5 }}
                  control={(
                    <Checkbox
                      size="small"
                      checked={(plan.coveredVehicleTypes || []).includes(t.key)}
                      onChange={(e) => toggleType(t.key, e.target.checked)}
                    />
                  )}
                  label={<Typography variant="body2">{t.label}</Typography>}
                />
              ))}
            </FormGroup>
          </Box>
        );
      })}

      <Divider sx={{ my: 1.5 }} />
      <FormLabel component="legend" sx={{ fontSize: 13, mb: 1 }}>Benefits</FormLabel>
      <Grid container spacing={1.5} sx={{ mb: 1 }}>
        <Grid item xs={6}>
          <TextField
            {...numField({ label: 'Fuel delivered' })} value={b.fuelGallons ?? ''}
            onChange={(e) => setBenefit('fuelGallons', e.target.value)}
            InputProps={{ endAdornment: <InputAdornment position="end">gal</InputAdornment> }}
            inputProps={{ min: 0, max: 50 }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            {...numField({ label: 'Trip interruption cap' })} value={b.tripInterruptionCap ?? ''}
            onChange={(e) => setBenefit('tripInterruptionCap', e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            inputProps={{ min: 0 }}
            helperText="0 = not included"
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            {...numField({ label: 'Tire & wheel cap' })} value={b.tireWheelCap ?? ''}
            onChange={(e) => setBenefit('tireWheelCap', e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            inputProps={{ min: 0 }}
            helperText="0 = not included"
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            {...numField({ label: 'Tire & wheel claims / yr' })} value={b.tireWheelPerYear ?? ''}
            onChange={(e) => setBenefit('tireWheelPerYear', e.target.value)}
            inputProps={{ min: 0, max: 20 }}
          />
        </Grid>
      </Grid>
      <FormGroup row>
        <FormControlLabel
          control={<Switch size="small" checked={!!b.mobileMechanic} onChange={(e) => setBenefit('mobileMechanic', e.target.checked)} />}
          label={<Typography variant="body2">Mobile mechanic</Typography>}
        />
        <FormControlLabel
          control={<Switch size="small" checked={b.household !== false} onChange={(e) => setBenefit('household', e.target.checked)} />}
          label={<Typography variant="body2">Household</Typography>}
        />
        <FormControlLabel
          control={<Switch size="small" checked={b.adFree !== false} onChange={(e) => setBenefit('adFree', e.target.checked)} />}
          label={<Typography variant="body2">Ad-free</Typography>}
        />
      </FormGroup>

      <Divider sx={{ my: 1.5 }} />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-all' }}>
        {stripe.productId ? (
          <>
            Stripe product: {stripe.productId}<br />
            Monthly price: {stripe.monthlyPriceId || '—'}<br />
            Annual price: {stripe.annualPriceId || '—'}
          </>
        ) : 'Stripe: Not synced yet — save to create'}
      </Typography>
    </Paper>
  );
}

function MembershipsSwitchCard({ enabled, setField }) {
  const on = enabled !== false;
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: on ? undefined : 'warning.main' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="subtitle1" sx={{ mr: 1 }}>Offer memberships</Typography>
        <FormControlLabel
          control={<Switch checked={on} onChange={(e) => setField('membershipsEnabled', e.target.checked)} />}
          label={on ? 'On — plans are offered' : 'Off — no plans are offered'}
        />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Off hides every subscription offer everywhere: no plans on wkroadss.com, in the customer app or on quotes, no intro offer,
        and no new sign-ups or plan changes. Customers pay per call. Anyone already subscribed keeps their covered calls and
        discount until they cancel. Switch it back on at any time; the plan settings below are kept.
      </Typography>
    </Paper>
  );
}

function IntroOfferCard({ intro, setField, membershipsEnabled }) {
  const value = intro || {};
  const set = (field, v) => setField('intro', { ...value, [field]: v });
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="subtitle1" sx={{ mr: 1 }}>Intro discount offer</Typography>
        <FormControlLabel
          control={<Switch size="small" checked={value.enabled !== false} onChange={(e) => set('enabled', e.target.checked)} />}
          label={value.enabled !== false ? 'On' : 'Off'}
        />
        <TextField
          {...numField({ label: 'Percent off', fullWidth: false })} value={value.percentOff ?? ''}
          onChange={(e) => set('percentOff', e.target.value)}
          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
          inputProps={{ min: 0, max: 100 }} sx={{ width: 140 }}
          disabled={value.enabled === false}
        />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        First-year discount shown next to every plan price. Applies once to annual plans and to the first 12 monthly payments;
        synced to Stripe coupons on save.{membershipsEnabled === false ? ' Not shown anywhere while memberships are off.' : ''}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, wordBreak: 'break-all' }}>
        Annual coupon: {value.stripeCouponAnnualId || 'not synced'} · Monthly coupon: {value.stripeCouponMonthlyId || 'not synced'}
        {value.stripeCouponPercent != null ? ` · created at ${value.stripeCouponPercent}%` : ''}
      </Typography>
    </Paper>
  );
}

function PlansSection({ config, setField }) {
  const [family, setFamily] = useState('auto');
  const plans = normalizePlans(config.plans);
  const vehicleTypes = vehicleTypesFrom(config);
  const familyDef = PLAN_FAMILIES.find((f) => f.key === family);

  const updatePlan = (next) => {
    setField('plans', plans.map((p) => (p.key === next.key ? next : p)));
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>Membership plans</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Changing a price creates a new Stripe price on save. Existing subscribers keep their current price. Each family has three fixed plans; the key and family are not editable.
      </Typography>

      <MembershipsSwitchCard enabled={config.membershipsEnabled} setField={setField} />
      <IntroOfferCard intro={config.intro} setField={setField} membershipsEnabled={config.membershipsEnabled} />

      <TextField
        {...numField({ label: 'Unlimited soft cap / year', fullWidth: false })}
        value={config.unlimitedSoftCapPerYear ?? ''}
        onChange={(e) => setField('unlimitedSoftCapPerYear', e.target.value)}
        inputProps={{ min: 0, max: 1000 }} sx={{ mb: 2, width: 260 }}
        helperText="Unlimited plans past this many covered calls in a plan year are flagged for review, never blocked"
      />

      <Tabs value={family} onChange={(e, v) => setFamily(v)} sx={{ mb: 2 }}>
        {PLAN_FAMILIES.map((f) => <Tab key={f.key} value={f.key} label={f.name} />)}
      </Tabs>

      <Grid container spacing={2}>
        {familyDef.planKeys.map((key) => {
          const plan = plans.find((p) => p.key === key);
          return (
            <Grid item xs={12} md={4} key={key}>
              <PlanCard plan={plan} intro={config.intro} vehicleTypes={vehicleTypes} onChange={updatePlan} />
            </Grid>
          );
        })}
      </Grid>
    </Paper>
  );
}

/** Shape the plans the way PUT /config expects them. */
export const plansPayload = (config) => normalizePlans(config.plans).map((p) => ({
  key: p.key,
  family: p.family,
  name: p.name,
  tagline: p.tagline || '',
  description: p.description || '',
  enabled: p.enabled !== false,
  sortOrder: p.sortOrder,
  prices: { monthly: Number(p.prices?.monthly), annual: Number(p.prices?.annual) },
  includedCallsPerYear: isBlank(p.includedCallsPerYear) ? null : Number(p.includedCallsPerYear),
  extraCallDiscountPercent: Number(p.extraCallDiscountPercent ?? 0),
  towMilesPerCall: Number(p.towMilesPerCall ?? 0),
  coveredVehicleTypes: p.coveredVehicleTypes || [],
  benefits: {
    fuelGallons: Number(p.benefits?.fuelGallons ?? 0),
    mobileMechanic: !!p.benefits?.mobileMechanic,
    tripInterruptionCap: Number(p.benefits?.tripInterruptionCap ?? 0),
    tireWheelCap: Number(p.benefits?.tireWheelCap ?? 0),
    tireWheelPerYear: Number(p.benefits?.tireWheelPerYear ?? 0),
    adFree: p.benefits?.adFree !== false,
    household: p.benefits?.household !== false
  }
}));

export default PlansSection;
